/* eslint-disable react/prop-types */
// @flow

import clsx from 'clsx';
import { CornerDownRight } from 'lucide-react';

import { Text } from '@gandi/ui';

import styles from './ListRow.scss';

const maxLength = 100;

type DecoratorProps = {
  children?: React.Node | void,
  className?: string,
  as?: 'div' | 'span' | 'ul' | 'p',
};

function Decorator({ children, className, as: Tag = 'div' }: DecoratorProps) {
  if (!children) return null;
  return <Tag className={className}>{children}</Tag>;
}

type Head =
  | { children: React.Node | string, title?: void, options?: void }
  | { title: string | React.Node, children?: void, options?: void }
  | {
      title: string | React.Node,
      options?: Array<React.Node>,
      children?: void,
    }
  | {
      title?: void,
      options: Array<React.Node>,
      children?: void,
    };

type Last =
  | { cta: React.Node, price?: void }
  | { cta?: void, price: React.Node }
  | { cta?: empty, price?: empty };

export type Props = {
  ...Head,
  ...Last,
  className?: string,
  /** A Lucide icon element */
  icon?: React.Node,
  badge?: React.Node,
  intent?: 'off' | 'success' | 'warning' | 'danger' | 'promotional' | 'muted',
  /** Should the options show a corner arrow (true by default) or just a small grey text. */
  optionsArrow?: boolean,
  density?: 's' | 'm',
};

export function ListRow({
  badge,
  children,
  cta,
  icon,
  density = 'm',
  intent = 'muted',
  options,
  optionsArrow = true,
  price,
  title,
  className,
}: Props): React.MixedElement {
  return (
    <div
      className={clsx(
        styles.root,
        styles[intent],
        styles[`density-${density}`],
        className,
      )}
    >
      <div
        className={clsx(
          styles.head,
          !!children &&
            typeof children === 'string' &&
            children.length > maxLength
            ? styles.long
            : undefined,
        )}
      >
        {icon}
        <Decorator className={styles.badge}>{badge}</Decorator>
        {(!!title || !!options?.length) && (
          <div
            className={clsx(
              styles.content,
              options && options.length > 1 && styles.hasOptionsList,
            )}
          >
            <Decorator className={styles.title}>{title}</Decorator>
            <Decorator className={styles.options} as="ul">
              {!!options?.length &&
                options.map(
                  (option, i) =>
                    !!option && (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={i}>
                        {optionsArrow && (
                          <CornerDownRight size={16} strokeWidth={1.25} />
                        )}
                        <Text
                          color={
                            typeof option === 'string' ? 'weak' : undefined
                          }
                        >
                          {option}
                        </Text>
                      </li>
                    ),
                )}
            </Decorator>
          </div>
        )}
        <Decorator>{children}</Decorator>
      </div>
      <Decorator className={styles.last}>{cta || price}</Decorator>
    </div>
  );
}
